import React, { useMemo } from 'react';
import { Cascader } from 'antd';
import { DefaultOptionType } from 'antd/es/cascader';

import './AttributePicker.css';
import { ANY, NIL, SELECT_DATE } from '../constants/constants';
import { TIME_DATATYPE } from '../constants/datatypes';
import { Datatype, Genre } from '../constants/types';

const filter = (inputValue: string, path: DefaultOptionType[]): boolean =>
  path.some(
    option =>
      option?.label &&
      `${option?.label}`.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
  );

export interface Props {
  attributes: { genres: Genre[] };
  onChange: (
    value: (string | number)[],
    selectedOptions?: DefaultOptionType[] | undefined,
  ) => void;
  placeholder?: string;
  showUneditable?: boolean;
  showUnlistable?: boolean;
  includeNilAndAny?: boolean;
  datatypesToExclude?: Datatype[];
  attributeTypesToExclude?: string[];
  excludeAttributes?: boolean;
}

const AttributePicker = ({
  attributes,
  onChange,
  placeholder,
  showUnlistable = false,
  showUneditable = false,
  includeNilAndAny = false,
  datatypesToExclude = [],
  attributeTypesToExclude = [],
  excludeAttributes = false,
  ...props
}: Props) => {
  const cascaderOptions = useMemo(
    () =>
      attributes.genres.flatMap(genre => {
        const genreOption: DefaultOptionType = {
          value: genre.name,
          label: genre.description,
        };
        const sections = genre.sections.flatMap(section => {
          const sectionOption: DefaultOptionType = {
            value: section.name,
            label: section.description,
          };
          const attributeTypes = section.attribute_types.flatMap(
            attributeType => {
              if (!showUneditable && !attributeType.is_editable) {
                return [];
              }
              if (attributeTypesToExclude.indexOf(attributeType.name) !== -1) {
                return [];
              }
              if (datatypesToExclude.indexOf(attributeType.datatype) !== -1) {
                return [];
              }
              if (!showUnlistable && attributeType.listable === false) {
                return [];
              }

              if (!attributeType.listable) {
                if (includeNilAndAny) {
                  const children = [
                    { value: ANY, label: 'Any' },
                    { value: NIL, label: 'Nil' },
                  ];

                  if (attributeType.datatype === TIME_DATATYPE) {
                    children.push({ value: SELECT_DATE, label: 'Select a date' });
                  }

                  return [
                    {
                      value: attributeType.name,
                      label: attributeType.description,
                      children,
                    },
                  ];
                }
                return [{
                  value: attributeType.name,
                  label: attributeType.description,
                }];
              }

              const attributeTypeOption: DefaultOptionType = {
                value: attributeType.name,
                label: attributeType.description,
                children: [],
              };
              const mappedAttributes = excludeAttributes
                ? []
                : attributeType.attributes.flatMap(attribute => ({
                  value: `${attribute.value}`,
                  label: attribute.value.toString(),
                  // attribute value may be a number
                }));

              if (includeNilAndAny) {
                mappedAttributes.push({ value: ANY, label: 'Any' });
                mappedAttributes.push({ value: NIL, label: 'Nil' });
              }

              if (mappedAttributes.length > 0) {
                attributeTypeOption.children = mappedAttributes;

                return [attributeTypeOption];
              }

              // Return attributeTypeOption if we're excluding attributes
              return excludeAttributes ? [attributeTypeOption] : [];
            }
          );

          if (attributeTypes.length > 0) {
            sectionOption.children = attributeTypes;
            return [sectionOption];
          }

          return [];
        });

        if (sections.length > 0) {
          genreOption.children = sections;
          return [genreOption];
        }

        return [];
      }),
    [
      attributes,
      includeNilAndAny,
      showUnlistable,
      showUneditable,
      datatypesToExclude,
      attributeTypesToExclude,
      excludeAttributes,
    ]
  );

  return (
    <Cascader
      data-testid="attributePicker"

      onChange={onChange}
      options={cascaderOptions}
      placeholder={placeholder}
      showSearch={{ filter }}
      size="large"
      value={[]}
      {...props}
    />
  );
};

export default AttributePicker;
